import React from 'react';
import UISectionWrapper, {
  UISectionHeader,
  UISectionBody,
  UISectionFooter,
  UIFormRow,
  UIFormLabels,
  UIFormFields,
  UIFormLabel
} from '@/components/shared/UISectionWrapper';
import UISectionTitle from '@/components/shared/UISectionTitle';
import UIInput from '@/components/shared/UIInput';
import UISelect from '@/components/shared/UISelect';
import UIButton from '@/components/shared/UIButton';
import UICheckbox from '@/components/shared/UICheckbox';
import UISpinner from '@/components/shared/UISpinner';
import TermsAndConditionsModal from '@/components/shared/TermsAndConditionsModal';
import { useProfile } from '@/hooks/useProfile';
import { countryOptions, provinceOptions } from '@/data/options';
import './PersonalInformationSection.scss';

export interface PersonalInformationSectionProps {
  className?: string;
}

const baseClass = 'PersonalInformationSection';

const PersonalInformationSection: React.FC<PersonalInformationSectionProps> = ({ className = '' }) => {
  const {
    data,
    initialData,
    loading,
    handleChange,
    handleSave,
    handleClear,
    getAgreementValue,
    setAgreementValue,
    isFormValid,
  } = useProfile('personal-details');

  const [showTermsModal, setShowTermsModal] = React.useState(false);
  const [modalType, setModalType] = React.useState<'terms' | 'privacy'>('terms');

  const handlePersonalInfoAgreementChange = (checked: boolean) => {
    setAgreementValue('personal_info_agreement', checked);
  };

  const handleTermsPrivacyAgreementChange = (checked: boolean) => {
    setAgreementValue('terms_privacy_agreement', checked);
  };

  const isValid = isFormValid();

  const hasChanges =
    data.personal_country !== initialData.personal_country ||
    data.personal_province !== initialData.personal_province ||
    data.personal_city !== initialData.personal_city ||
    data.personal_address !== initialData.personal_address ||
    data.personal_apt_suite !== initialData.personal_apt_suite ||
    data.personal_unit !== initialData.personal_unit ||
    data.personal_municipality !== initialData.personal_municipality ||
    data.personal_postal_code !== initialData.personal_postal_code ||
    data.personal_phone !== initialData.personal_phone ||
    data.personal_info_agreement !== initialData.personal_info_agreement ||
    data.terms_privacy_agreement !== initialData.terms_privacy_agreement;

  const handleTermsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setModalType('terms');
    setShowTermsModal(true);
  };

  const handlePrivacyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setModalType('privacy');
    setShowTermsModal(true);
  };

  const handleTermsAccept = () => {
    setAgreementValue('terms_privacy_agreement', true);
    setShowTermsModal(false);
  };

  if (loading) {
    return (
      <div className={`${baseClass}__loading`} style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <UISpinner />
      </div>
    );
  }

  return (
    <UISectionWrapper
      className={`${baseClass} ${className}`}
      layout="form-horizontal"
      formPadding="large"
      labelWidth="120px"
      formGap="15px"
      align="stretch"
    >
      <UISectionHeader>
        <UISectionTitle
          title="Personal information"
          description="Update some personal information. Your address will never be publicly available."
        />
      </UISectionHeader>

      <UISectionBody>
        <UIFormRow>
          <UIFormLabels>
            <UIFormLabel>Country</UIFormLabel>
            <UIFormLabel>Province</UIFormLabel>
            <UIFormLabel>City</UIFormLabel>
            <UIFormLabel>Address</UIFormLabel>
            <UIFormLabel>Municipality</UIFormLabel>
            <UIFormLabel>Postal code</UIFormLabel>
            <UIFormLabel>Telephone No.</UIFormLabel>
            <UIFormLabel>&nbsp;</UIFormLabel>
            <UIFormLabel>&nbsp;</UIFormLabel>
          </UIFormLabels>

          <UIFormFields>
            <UISelect
              options={countryOptions}
              placeholder="Select country"
              fullWidth
              value={data.personal_country || ''}
              onChange={(e) => handleChange('personal_country', e.target.value)}
            />
            <UISelect
              options={provinceOptions}
              placeholder="Select province"
              fullWidth
              value={data.personal_province || ''}
              onChange={(e) => handleChange('personal_province', e.target.value)}
            />
            <UIInput
              placeholder="City"
              fullWidth
              value={data.personal_city || ''}
              onChange={(e) => handleChange('personal_city', e.target.value)}
            />
            <div className={`${baseClass}__address-row`}>
              <UIInput
                placeholder="Address"
                className={`${baseClass}__address-row-small`}
                value={data.personal_address || ''}
                onChange={(e) => handleChange('personal_address', e.target.value)}
              />
              <UIInput
                placeholder="Apt, suite"
                className={`${baseClass}__address-row-large`}
                value={data.personal_apt_suite || ''}
                onChange={(e) => handleChange('personal_apt_suite', e.target.value)}
              />
              <UIInput
                placeholder="Unit"
                className={`${baseClass}__address-row-small`}
                value={data.personal_unit || ''}
                onChange={(e) => handleChange('personal_unit', e.target.value)}
              />
            </div>
            <UIInput
              placeholder="Municipality"
              fullWidth
              value={data.personal_municipality || ''}
              onChange={(e) => handleChange('personal_municipality', e.target.value)}
            />
            <UIInput
              placeholder="Postal code"
              fullWidth
              value={data.personal_postal_code || ''}
              onChange={(e) => handleChange('personal_postal_code', e.target.value)}
            />
            <UIInput
              placeholder="Telephone No."
              fullWidth
              value={data.personal_phone || ''}
              onChange={(e) => handleChange('personal_phone', e.target.value)}
            />

            <div className={`${baseClass}__agreement-checkbox`}>
              <UICheckbox
                size="medium"
                checked={getAgreementValue('personal_info_agreement')}
                onChange={(e) => handlePersonalInfoAgreementChange(e.target.checked)}
                label="I authorize PropSavvy to store my personal information."
              />
            </div>

            <div className={`${baseClass}__agreement-with-links`}>
              <UICheckbox
                size="medium"
                checked={getAgreementValue('terms_privacy_agreement')}
                onChange={(e) => handleTermsPrivacyAgreementChange(e.target.checked)}
                label=""
              />
              <span className={`${baseClass}__agreement-text`}>
                I agree to{' '}
                <a
                  href="#"
                  onClick={handleTermsClick}
                  className={`${baseClass}__legal-link`}
                >
                  Terms of Service
                </a>
                {' '}
                and{' '}
                <a
                  href="#"
                  onClick={handlePrivacyClick}
                  className={`${baseClass}__legal-link`}
                >
                  Privacy Policy
                </a>
                .
              </span>
            </div>
          </UIFormFields>
        </UIFormRow>
      </UISectionBody>

      <UISectionFooter>
        <div className={`${baseClass}__buttons`}>
          <UIButton
            variant="primary"
            onClick={handleSave}
            disabled={!isValid || !hasChanges}
          >
            Save
          </UIButton>
          <UIButton variant="primary" outline onClick={handleClear}>
            Clear
          </UIButton>
        </div>
      </UISectionFooter>

      <TermsAndConditionsModal
        isOpen={showTermsModal}
        type={modalType}
        onClose={() => setShowTermsModal(false)}
        onAccept={handleTermsAccept}
      />
    </UISectionWrapper>
  );
};

export default PersonalInformationSection;